// src/pages/VerifyEmail.js
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { toast, ToastContainer } from 'react-toastify';
import { clearCurrentUserProfile, getPostVerificationRoute, loadUserProfile, persistCurrentUserProfile } from '../utils/authVerification';
import RegistrationProgress from '../components/RegistrationProgress';


export default function VerifyEmail() {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const emailParam = new URLSearchParams(window.location.search).get('email');
  const fallback = localStorage.getItem('pendingEmail') || auth.currentUser?.email || '';
  const displayEmail = emailParam || fallback;
  const [cooldown, setCooldown] = useState(0);

  const routeAfterEmailVerification = useCallback(async (user) => {
    const profileData = await loadUserProfile(user.uid);
    if (!profileData) {
      clearCurrentUserProfile();
      toast.error('User profile not found. Please sign in again.');
      navigate('/login', { replace: true });
      return;
    }

    const nextRoute = getPostVerificationRoute(user, profileData);

    persistCurrentUserProfile(user.uid, profileData);
    navigate(nextRoute, { replace: true });
  }, [navigate]);


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }
      await user.reload();
      if (user.emailVerified) {
        toast.success('Email verified!');
        localStorage.removeItem('pendingEmail');
        await routeAfterEmailVerification(user);
      }
    });
    return () => unsub();
  }, [routeAfterEmailVerification]);

  const resend = async () => {
  if (cooldown > 0) {
    return toast.error(`Please wait ${cooldown}s before trying again.`);
  }
  const u = auth.currentUser;
  if (!u) {
    console.error('Resend clicked but no currentUser.');
    return toast.error('No logged-in user found.');
  }
  try {
    setSending(true);
    await u.reload();

    await sendEmailVerification(u);
    console.log('Resent verification email:', displayEmail);

    toast.success('Verification email resent (check spam).');

    // start cooldown
    setCooldown(60);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

  } catch (e) {
    console.error('Email resend failed:', e);
    toast.error(`Email resend failed: ${e?.message || e}`);
  } finally {
    setSending(false);
  }
};

  const checkVerifiedNow = async () => {
    const u = auth.currentUser;
    if (!u) {
      toast.error('No logged-in user found. Please sign in again.');
      return;
    }
    try {
      setChecking(true);
      await u.reload();
      if (u.emailVerified) {
        toast.success('Email verified!');
        localStorage.removeItem('pendingEmail');
        await routeAfterEmailVerification(u);
      } else {
        toast.warning('Email not verified yet. Please click the link in your email, then tap this button again.');
      }
    } catch (e) {
      console.error('check verified failed:', e);
      toast.error(e?.message || 'Could not check verification status.');
    } finally {
      setChecking(false);
    }
  };

  const backToLogin = async () => {
    try {
      if (auth.currentUser) {
        try {
          await auth.currentUser.reload();
        } catch (_) {}
        try {
          await auth.signOut();
        } catch (_) {}
      }
      localStorage.removeItem('pendingEmail');
      navigate('/login', { replace: true });
    } catch (e) {
      console.error('backToLogin error:', e);
      toast.error(e?.message || 'Failed to return to sign in.');
    }
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <RegistrationProgress currentStep="email" className="mb-4" />
        <div className="bg-white rounded-xl p-6 shadow w-full">
        <h2 className="text-xl font-semibold mb-2">Verify your email</h2>
       <p className="text-sm text-gray-600 mb-4">
  We sent a verification link to <b>{displayEmail || 'your email address'}</b>.<br />
  Please check your inbox <strong>and your spam/junk folder</strong>.<br />
  After clicking the link, return to this page and refresh it if your account does not unlock automatically.
</p>
       <button
  disabled={sending || cooldown > 0}
  onClick={resend}
  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
>
  {sending
    ? 'Sending…'
    : cooldown > 0
      ? `Email resend available in ${cooldown}s`
      : 'Resend verification email'}
</button>

        <button
          disabled={checking}
          onClick={checkVerifiedNow}
          className="mt-3 px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 w-full"
        >
          {checking ? 'Checking…' : "I've verified my email"}
        </button>

        <button
          onClick={backToLogin}
          className="mt-3 px-4 py-2 rounded border border-gray-300 text-gray-700 w-full"
        >
          Back to sign in
        </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
